import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { ApolloLink, Reference } from '@apollo/client';
import { gql } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import type {
  DataGridPremiumProps,
  GridApi,
  GridFilterModel,
  GridGetRowsParams,
  GridGetRowsResponse,
  GridSortModel,
} from '@mui/x-data-grid-premium';
import { GridLogicOperator } from '@mui/x-data-grid-premium';
import { isEqual } from 'lodash';
import { uniqBy, uniqWith } from 'ramda';
import type { PartySubset } from 'src/components';
import { splitPendingParties } from 'src/components/parties/pending-party';
import { Apollo } from 'src/gql/generated';
import { documentTypename } from 'src/helpers';
import { Sentry } from 'src/sentry';
import type { ReactSetStateTuple } from 'src/types';
import { useDebounce } from 'use-debounce';

export function useGetParties(options?: Parameters<typeof Apollo.useGetPartiesQuery>[0]): {
  loading: boolean;
  parties: NonNullable<Apollo.GetPartiesQueryHookResult['data']>['parties'];
  refetch: Apollo.GetPartiesQueryHookResult['refetch'];
} {
  const { data, loading, refetch } = Apollo.useGetPartiesQuery(options);
  return { loading, parties: data?.parties ?? [], refetch };
}

export type PartySearchOption = Apollo.SearchPartiesQuery['searchParties'][number];

export const partySearchLimit = 50;

const partySearchDebounceMs = 150;

/**
 * Server-side, offset-paginated party search for type-ahead pickers. Holds the
 * accumulated pages in local state so a picker can lazily load more on scroll
 * without the full party list ever reaching the client. The search term is
 * debounced; changing it (or reopening the picker) resets pagination to the
 * first page. A first page already in the Apollo cache is shown at once while
 * the network refreshes it, so reopening a picker or backspacing to an earlier
 * term never waits. A per-request id guard ensures only the latest fetch
 * applies, so fast typing or scrolling never races stale results into the list.
 */
export function usePartySearch(options?: { skip?: boolean }): {
  hasMore: boolean;
  loading: boolean;
  loadMore: () => void;
  parties: PartySearchOption[];
  refetch: () => void;
  /** The search term `parties` belongs to, or null before the first page arrives. */
  resultsSearch: string | null;
  search: string;
  setSearch: (value: string) => void;
} {
  const client = useApolloClient();
  const skip = options?.skip ?? false;
  const [search, setSearch] = useState<string>('');
  const [debouncedSearch] = useDebounce(search, partySearchDebounceMs);
  const trimmedSearch = debouncedSearch.trim();
  const [parties, setParties] = useState<PartySearchOption[]>([]);
  const [resultsSearch, setResultsSearch] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const requestIdRef = useRef<number>(0);

  const fetchPage = useCallback(
    async (offset: number) => {
      const requestId = ++requestIdRef.current;
      const variables: Apollo.SearchPartiesQueryVariables = {
        limit: partySearchLimit,
        offset,
        query: trimmedSearch,
      };
      if (offset === 0) {
        const cached = client.readQuery<
          Apollo.SearchPartiesQuery,
          Apollo.SearchPartiesQueryVariables
        >({ query: Apollo.SearchPartiesDocument, variables });
        if (cached) {
          setParties(cached.searchParties);
          setResultsSearch(trimmedSearch);
          setHasMore(cached.searchParties.length === partySearchLimit);
        }
      }
      setLoading(true);
      try {
        const { data } = await client.query<
          Apollo.SearchPartiesQuery,
          Apollo.SearchPartiesQueryVariables
        >({
          fetchPolicy: 'network-only',
          query: Apollo.SearchPartiesDocument,
          variables,
        });
        // A newer request started while this was in flight — discard stale results.
        if (requestId !== requestIdRef.current) return;

        const page = data?.searchParties ?? [];
        setParties(previous =>
          offset === 0 ? page : uniqBy(party => party.id, [...previous, ...page])
        );
        setResultsSearch(trimmedSearch);
        setHasMore(page.length === partySearchLimit);
      } catch (error) {
        if (requestId === requestIdRef.current) {
          console.error('Error searching parties:', error);
          Sentry.captureException(error);
        }
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    },
    [client, trimmedSearch]
  );

  useEffect(() => {
    if (skip) {
      // Invalidate any in-flight request and clear accumulated pages.
      requestIdRef.current += 1;
      setParties([]);
      setResultsSearch(null);
      setHasMore(false);
      setLoading(false);
      return;
    }
    void fetchPage(0);
  }, [fetchPage, skip]);

  const loadMore = useCallback(() => {
    if (skip || loading || !hasMore) return;
    void fetchPage(parties.length);
  }, [fetchPage, hasMore, loading, parties.length, skip]);

  /**
   * Re-fetch the first page with the current term — e.g. after creating a party
   * so it shows up immediately in an open picker.
   */
  const refetch = useCallback(() => {
    if (skip) return;
    void fetchPage(0);
  }, [fetchPage, skip]);

  return { hasMore, loadMore, loading, parties, refetch, resultsSearch, search, setSearch };
}

/**
 * Server-side data source for the parties grid, mirroring `useWorkflowDataSource`.
 * Drives `DataGridPremium`'s `lazyLoading` via cursor-based pagination (the cursor
 * is the id of the last row from the previous page) and keeps already-rendered rows
 * in sync with the Apollo cache.
 */
export function usePartyDataSource(options: {
  apiRef: React.MutableRefObject<GridApi | null>;
  initialSortModel?: GridSortModel;
}): {
  dataSource: DataGridPremiumProps['dataSource'];
  decrementTotalCount: (by: number) => void;
  filterModelTuple: ReactSetStateTuple<GridFilterModel>;
  sortModelTuple: ReactSetStateTuple<GridSortModel>;
  totalCount: number;
  onDataSourceError?: DataGridPremiumProps['onDataSourceError'];
} {
  const client = useApolloClient();
  const { apiRef, initialSortModel } = options;
  const [totalCount, setTotalCount] = useState<number>(0);
  const prevSortModelRef = useRef<GridSortModel | null>(null);
  const [sortModel, setSortModel] = useState<GridSortModel>(initialSortModel ?? []);
  const prevFilterModelRef = useRef<GridFilterModel | null>(null);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
    logicOperator: GridLogicOperator.And,
    quickFilterValues: [],
  });

  useEffect(() => {
    const observable = client.watchQuery<
      Apollo.GetPartyRowsQuery,
      Apollo.GetPartyRowsQueryVariables
    >({
      fetchPolicy: 'cache-only',
      query: Apollo.GetPartyRowsDocument,
      variables: { filterModel, sortModel },
    });
    const subscription = observable.subscribe({
      error: err => {
        console.error('Error watching party cache:', err);
        Sentry.captureException(err);
      },
      next: ({ data }) => {
        const updatedParties = data?.partyRows?.rows ?? [];
        const rowsToUpdate = updatedParties.flatMap(party => {
          const id = party?.id;
          if (!id) return [];
          const existingRow = apiRef.current?.getRow(id);
          if (!existingRow) return [];

          // Skip identical data — prevents an infinite fetch loop from cache merges creating new refs.
          if (isEqual(existingRow, party)) return [];

          // updateRows merges by id, so a partial cache row is a valid update.
          return [{ ...party, id }];
        });
        if (rowsToUpdate.length) apiRef.current?.updateRows(rowsToUpdate);
      },
    });

    return () => subscription.unsubscribe();
  }, [apiRef, client, filterModel, sortModel]);

  const safeGetRows = useCallback(
    async (params: GridGetRowsParams): Promise<GridGetRowsResponse> => {
      const { end, filterModel, paginationModel, sortModel, start } = params;
      const startRowId =
        typeof start === 'number' && start > 0
          ? apiRef.current?.getRowIdFromRowIndex(start - 1)
          : null;
      const safePageSize = paginationModel?.pageSize ?? 50;
      const sortChanged = !isEqual(prevSortModelRef.current, sortModel);
      const filterChanged = !isEqual(prevFilterModelRef.current, filterModel);
      const safeCursor = !sortChanged && !filterChanged ? (startRowId?.toString() ?? null) : null;
      const limit = typeof start === 'number' ? end - start + 1 : safePageSize;

      if (filterChanged || sortChanged) apiRef.current?.dataSource?.cache?.clear();

      prevSortModelRef.current = sortModel;
      prevFilterModelRef.current = filterModel;

      const { data } = await client.query<
        Apollo.GetPartyRowsQuery,
        Apollo.GetPartyRowsQueryVariables
      >({
        fetchPolicy: 'network-only',
        query: Apollo.GetPartyRowsDocument,
        variables: { cursor: safeCursor, filterModel, limit, sortModel },
      });
      const allCachedRows = data?.partyRows?.rows ?? [];
      const cursorIndex = safeCursor
        ? allCachedRows.findIndex(party => party.id === safeCursor)
        : -1;
      /**
       * With a found cursor, return the page after it. Otherwise (the first
       * page, or a cursor that is somehow missing from the cache) return at most
       * `limit` rows so we never ingest the entire accumulated cache at once.
       */
      const newRows =
        cursorIndex !== -1
          ? allCachedRows.slice(cursorIndex + 1, cursorIndex + 1 + limit)
          : allCachedRows.slice(0, limit);

      setTotalCount(data?.partyRows?.meta?.totalCount ?? 0);

      return {
        rowCount: undefined,
        rows: newRows,
      };
    },
    [apiRef, client]
  );

  const onDataSourceError = useCallback((error: Error) => {
    console.error(error);
    Sentry.captureException(error);
  }, []);
  const dataSource = useMemo(() => ({ getRows: safeGetRows }), [safeGetRows]);
  /**
   * The footer count is local state (set on fetch), so a local row removal must
   * decrement it directly — the cache prune alone won't move the displayed value.
   */
  const decrementTotalCount = useCallback((by: number) => {
    setTotalCount(previous => Math.max(0, previous - by));
  }, []);

  return {
    dataSource,
    decrementTotalCount,
    filterModelTuple: [filterModel, setFilterModel],
    onDataSourceError,
    sortModelTuple: [sortModel, setSortModel],
    totalCount,
  };
}

export function useGetParty(
  partyId: UUID,
  options?: Omit<NonNullable<Parameters<typeof Apollo.useGetPartyQuery>[0]>, 'variables'>
): {
  loading: boolean;
  party: NonNullable<Apollo.GetPartyQueryHookResult['data']>['party'];
} {
  const { data, loading } = Apollo.useGetPartyQuery({ ...options, variables: { id: partyId } });
  return { loading, party: data?.party ?? null };
}

type PartyWithDocuments = NonNullable<
  NonNullable<Apollo.GetPartyDocumentsQueryHookResult['data']>['party']
>;

export function useGetPartyDocuments(
  partyId: UUID,
  options?: Omit<NonNullable<Parameters<typeof Apollo.useGetPartyDocumentsQuery>[0]>, 'variables'>
): {
  documents: PartyWithDocuments['documents'];
  loading: boolean;
  party: PartyWithDocuments | null;
} {
  const { data, loading } = Apollo.useGetPartyDocumentsQuery({
    ...options,
    variables: { id: partyId },
  });
  return { documents: data?.party?.documents ?? [], loading, party: data?.party ?? null };
}

type DocumentWithParties = NonNullable<
  NonNullable<Apollo.GetDocumentWithPartiesQueryHookResult['data']>['document']
>;

export function useGetDocumentParties(
  documentId: UUID | null,
  options?: Omit<
    NonNullable<Parameters<typeof Apollo.useGetDocumentWithPartiesQuery>[0]>,
    'variables'
  >
): {
  document: DocumentWithParties | null;
  loading: boolean;
  parties: DocumentWithParties['parties'];
} {
  const { data, loading } = Apollo.useGetDocumentWithPartiesQuery({
    ...options,
    skip: !documentId,
    variables: { documentId: documentId ?? '' },
  });
  return { document: data?.document ?? null, loading, parties: data?.document?.parties ?? [] };
}

export function useGetWorkflowParties(
  workflowId: UUID | null,
  options?: Omit<
    NonNullable<Parameters<typeof Apollo.useGetWorkflowWithPartiesQuery>[0]>,
    'variables'
  >
) {
  const { data, loading } = Apollo.useGetWorkflowWithPartiesQuery({
    ...options,
    skip: !workflowId,
    variables: { workflowId: workflowId ?? '' },
  });
  return {
    files: data?.workflow?.files ?? [],
    loading,
    parties: data?.workflow?.parties ?? [],
    workflow: data?.workflow ?? null,
  };
}

export function useCreateParty(): {
  createParty: (
    options: Parameters<Apollo.CreatePartyMutationHookResult[0]>[0]
  ) => Promise<ApolloLink.Result<Apollo.CreatePartyMutation>>;
  loading: boolean;
} {
  const [createParty, { loading }] = Apollo.useCreatePartyMutation();

  return {
    createParty: options => {
      return createParty({
        ...options,
        update: (cache, { data }) => {
          if (!data) return;

          cache.modify({
            fields: {
              parties: existingPartyRefs => {
                return [
                  ...existingPartyRefs,
                  cache.writeFragment({
                    data: data.createParty,
                    fragment: gql`
                      fragment NewParty on Party {
                        __typename
                        id
                      }
                    `,
                  }),
                ];
              },
            },
          });
        },
      });
    },
    loading,
  };
}

export function useAddPartyToDocument(): {
  addPartyToDocument: (
    options: Parameters<Apollo.AddPartyToDocumentMutationHookResult[0]>[0]
  ) => Promise<ApolloLink.Result<Apollo.AddPartyToDocumentMutation>>;
  loading: boolean;
} {
  const [addPartyToDocument, { loading }] = Apollo.useAddPartyToDocumentMutation();

  return {
    addPartyToDocument: options => {
      return addPartyToDocument({
        ...options,
        update: (cache, { data }) => {
          if (!data) return;

          cache.modify({
            fields: {
              parties: (refs, { readField }) => {
                const partyRef = cache.writeFragment({
                  data: data.addPartyToDocument,
                  fragment: gql`
                    fragment NewParty on Party {
                      __typename
                      id
                    }
                  `,
                });

                return uniqWith(
                  (a, b) => readField<UUID>('id', a) === readField<UUID>('id', b),
                  [...refs, partyRef]
                );
              },
            },
            id: cache.identify({
              __typename: documentTypename,
              id: options?.variables?.input.documentId,
            }),
          });
        },
      });
    },
    loading,
  };
}

void gql`
  query GetWorkflowDocumentsBasic($workflowId: UUID!) {
    workflow(workflowId: $workflowId) {
      __typename
      id
      files {
        __typename
        id
        document {
          __typename
          id
          isCertificate
        }
      }
    }
  }
`;

export interface WorkflowFileSubset extends Pick<Apollo.WorkflowFile, 'id' | '__typename'> {
  document: Pick<Apollo.Document, 'id' | 'name' | 'variant' | '__typename'> & {
    latestVersion:
      | Pick<Apollo.DocumentVersion, 'id' | '__typename'>
      | (Pick<Apollo.StaticFileVersion, 'id' | '__typename'> & {
          fileLocation: Pick<Apollo.DocFile, 'mimetype' | '__typename'>;
        });
  };
}

export function useSetWorkflowParties() {
  const [setWorkflowParties, { loading }] = Apollo.useSetWorkflowPartiesMutation();
  const getOptimisticParties = useGetOptimisticParties();
  const safeSetWorkflowParties = useCallback(
    ({
      files,
      parties,
      workflowId,
    }: {
      files: WorkflowFileSubset[];
      parties: PartySubset[];
      workflowId: UUID;
    }) => {
      const optimisticParties = getOptimisticParties(parties);
      return setWorkflowParties({
        optimisticResponse: {
          __typename: 'Mutation',
          setWorkflowParties: {
            __typename: 'Workflow',
            files: files.map(file => ({
              __typename: 'WorkflowFile',
              document: {
                __typename: 'Document',
                id: file.document.id,
                parties: optimisticParties,
              },
              id: file.id,
            })),
            id: workflowId,
            parties: optimisticParties,
          },
        },
        variables: { input: { ...splitPendingParties(parties), workflowId } },
      });
    },
    [getOptimisticParties, setWorkflowParties]
  );

  return {
    loading,
    setWorkflowParties: safeSetWorkflowParties,
  };
}

export function useSetDocumentParties() {
  const [setDocumentParties, { loading }] = Apollo.useSetDocumentPartiesMutation();
  const getOptimisticParties = useGetOptimisticParties();
  const safeSetDocumentParties = useCallback(
    ({ documentId, parties }: { documentId: UUID; parties: PartySubset[] }) => {
      return setDocumentParties({
        optimisticResponse: {
          __typename: 'Mutation',
          setDocumentParties: {
            __typename: 'Document',
            id: documentId,
            parties: getOptimisticParties(parties),
          },
        },
        variables: { input: { documentId, ...splitPendingParties(parties) } },
      });
    },
    [getOptimisticParties, setDocumentParties]
  );

  return {
    loading,
    setDocumentParties: safeSetDocumentParties,
  };
}

export function useBulkDeleteParties() {
  const [bulkDeleteParties, { loading }] = Apollo.useBulkDeletePartiesMutation();

  return {
    bulkDeleteParties: (partyIds: UUID[]) => {
      return bulkDeleteParties({
        update: cache => {
          cache.modify({
            fields: {
              parties: (existingParties: readonly Reference[] = [], { readField }) => {
                return existingParties.filter((existingParty: Reference) => {
                  const id = readField<UUID>('id', existingParty);
                  return id && !partyIds.includes(id);
                });
              },
            },
          });
        },
        variables: { input: { partyIds } },
      });
    },
    loading,
  };
}

export function useMergeParties(): {
  loading: boolean;
  mergeParties: (
    options: Parameters<Apollo.MergePartiesMutationHookResult[0]>[0]
  ) => Promise<ApolloLink.Result<Apollo.MergePartiesMutation>>;
} {
  const [mergeParties, { loading }] = Apollo.useMergePartiesMutation();

  return {
    loading,
    mergeParties: options => {
      return mergeParties({
        ...options,
        update: (cache, { data }) => {
          // Mutation failed. Don't update cache.
          if (!data) return;

          const partiesKey: keyof Apollo.Query = 'parties';
          const sourcePartyIds = new Set(options?.variables?.input.sourcePartyIds ?? []);

          cache.modify({
            fields: {
              [partiesKey]: (refs: readonly Reference[] = [], { readField }) => {
                return refs.filter((ref: Reference) => {
                  const partyId = readField<UUID>('id', ref);
                  return partyId && !sourcePartyIds.has(partyId);
                });
              },
            },
          });
        },
      });
    },
  };
}

/**
 * The set-parties mutations return full `PartySimple` parties, so their
 * optimistic responses need a description too. Reuse the cached one so an
 * optimistic write never blanks it; a pending party has none yet.
 */
function useGetOptimisticParties() {
  const client = useApolloClient();

  return useCallback(
    (parties: readonly PartySubset[]): Apollo.PartySimpleFragment[] =>
      parties.map(party => {
        const cached = client.readFragment<Apollo.PartySimpleFragment>({
          fragment: Apollo.PartySimpleFragmentDoc,
          id: client.cache.identify(party),
        });
        return { ...party, description: cached?.description ?? '' };
      }),
    [client]
  );
}
